import React, { useState } from 'react';
import { Edit2, Trash2, X, Check } from 'lucide-react';
import type { ListItem as ListItemType } from '../../../types/topic';

interface ListItemProps {
  item: ListItemType;
  itemToDelete: string | null;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onEdit: (id: string, text: string) => void;
}

export const ListItem = React.memo(function ListItem({
  item,
  itemToDelete,
  onDelete,
  onDeleteClick,
  onCancelDelete,
  onEdit,
}: ListItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editInput, setEditInput] = useState(item.text);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editInput.trim()) return;
    onEdit(item.id, editInput);
    setIsEditing(false);
  };

  return (
    <div className="flex items-start">
      <div className="group inline-flex items-center gap-2 bg-white rounded-lg transition-all duration-200 hover:bg-gray-50 shadow-sm">
        <div className="px-4 py-3">
          {isEditing ? (
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <div className="flex items-center gap-4">
                <div className="w-2 h-2 rounded-full bg-blue-500/20" />
                <input
                  type="text"
                  value={editInput}
                  onChange={e => setEditInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setIsEditing(false);
                      setEditInput(item.text);
                    }
                  }}
                  className="bg-transparent text-base focus:outline-none min-w-[200px] font-montserrat"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditInput(item.text);
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!editInput.trim()}
                  className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1.5"
                >
                  <Check size={14} />
                  <span>Save</span>
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-2 h-2 rounded-full bg-blue-500/20" />
              <span className="text-gray-900 text-base font-montserrat">{item.text}</span>
            </div>
          )}
        </div>

        {!isEditing && (
          <div className="flex items-center space-x-2 pl-2 pr-4">
            {itemToDelete === item.id ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onDelete(item.id)}
                  className="p-1 text-red-600 hover:text-red-700 rounded"
                  title="Confirm delete"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={onCancelDelete}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-gray-500 hover:text-gray-700 rounded"
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => onDeleteClick(item.id)}
                  className="p-1 text-red-500 hover:text-red-700 rounded"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
